import { trackEventFromState } from '@eventbrite/site-analytics';
import { $FixMe } from '@eventbrite/ts-utils';
import loadable from '@loadable/component';
import { History } from 'history';
import React from 'react';
import { connect } from 'react-redux';
import {
    GA_SEARCH_CATEGORY,
    SEARCH_EVENT_NAVIGATION,
} from '../../../../constants/analytics';
import { trackEventClickInternal as trackEventClickInternalAction } from '../../../../redux/actions/analytics';
import { getEventExpansions as getEventExpansionsAction } from '../../../../redux/actions/expansions';
import { addSearchIdToGaAction } from '../../utils/analytics';
import { PrimarySearchResultsEventsContainer } from '../PrimarySearchResultsEventsContainer';
import { SearchHeaderContainer } from '../SearchHeaderContainer';
import './SearchResultsPanelContainer.scss';

const LazyTopMatchContainer = loadable(() => import('../TopMatchContainer'), {
    resolveComponent: (components: { TopMatchContainer: React.ReactElement }) =>
        components.TopMatchContainer,
});

const LazyPromotedResultsContainer = loadable(
    () => import('../PromotedResultsContainer'),
    {
        resolveComponent: (components: {
            PromotedResultsContainer: React.ReactElement;
        }) => components.PromotedResultsContainer,
    },
);

interface ResultsPanelProps {
    currentPage?: number;
    getEventExpansions: Function;
    hasTopMatch?: boolean;
    hasPromotedResults?: boolean;
    history?: History;
    isLoading?: boolean;
    onTrackEventClickInternal?: Function;
    trackGAEvent?: Function;
}

export class ResultsPanel extends React.PureComponent<ResultsPanelProps> {
    componentDidMount() {
        window.requestIdleCallback
            ? window.requestIdleCallback(this._handleIdleExpansion)
            : this.props.getEventExpansions();
    }

    _handleIdleExpansion = (_deadline: IdleDeadline) => {
        //No need to leverage deadline here as there is no
        //timeout set. We are okay to delay this request
        //from happening until the browser is otherwise happy
        this.props.getEventExpansions();
    };

    _handleTrackClick = (
        index: number,
        eventId: string,
        action: string,
        organizerFollowerCount: number,
    ) => {
        const { trackGAEvent } = this.props;

        trackGAEvent?.({
            action,
            category: GA_SEARCH_CATEGORY,
            label: eventId,
            dimensions: {
                eventId,
            },
            metrics: {
                searchRank: index,
                organizerFollowerCount: organizerFollowerCount || 0,
            },
        });
    };

    _handleTrackClickInternal = (
        index: number,
        id: string,
        ticketsBy: string,
    ) => {
        const { onTrackEventClickInternal } = this.props;

        const trackingData = {
            clickRank: index,
            navigation: SEARCH_EVENT_NAVIGATION,
            itemId: id,
        };

        onTrackEventClickInternal?.(trackingData, ticketsBy);
    };

    render() {
        const { history, isLoading, hasTopMatch, hasPromotedResults } =
            this.props;

        const renderTopMatch = hasTopMatch && !isLoading;
        const renderPromotedResults = hasPromotedResults && !isLoading;

        return (
            <div className="search-results-panel-content">
                <SearchHeaderContainer history={history} />
                {renderTopMatch && <LazyTopMatchContainer />}
                {renderPromotedResults && <LazyPromotedResultsContainer />}
                <PrimarySearchResultsEventsContainer
                    trackEventClick={this._handleTrackClick}
                    trackEventClickInternal={this._handleTrackClickInternal}
                    history={history}
                />
            </div>
        );
    }
}

export const _mapStateToProps = (state: any, ownProps: any) => {
    const hasTopMatch = state.content.entityContext.some(
        (context: { topMatchIds: string[] }) =>
            !!context.topMatchIds && context.topMatchIds.length > 0,
    );

    const hasPromotedResults = state.content.entityContext.some(
        (context: { promotedResultsIds: string[] }) =>
            !!context.promotedResultsIds &&
            context.promotedResultsIds.length > 0,
    );

    return {
        history: ownProps.history,
        currentPage: state.pagination.currentPage,
        hasTopMatch,
        hasPromotedResults,
        isLoading: state.pagination.loading,
        eventSearch: state.search.eventSearch,
        searchId: state.search.searchId,
        trackGAEvent: (action: any) =>
            trackEventFromState(state, addSearchIdToGaAction(action, state)),
    };
};

const _mapDispatchToProps = (dispatch: Function) => ({
    onTrackEventClickInternal: (...args: [$FixMe]) =>
        dispatch(trackEventClickInternalAction(...args)),
    getEventExpansions: () => dispatch(getEventExpansionsAction()),
});

export const SearchResultsPanelContainer = connect(
    _mapStateToProps,
    _mapDispatchToProps,
)(ResultsPanel);
